import type { FormEvent } from 'react';
import { useTypedAppForm } from '@gonfalon/forms/hooks';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { useMarketingFormSubmit } from '@gonfalon/marketing-forms/useMarketingFormSubmit';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Button, Form } from '@launchpad-ui/components';
import { z } from 'zod';

import { trackSignupEnterpriseDemoFormSubmitted } from '../analytics';

import styles from './EnterpriseDemoSignupForm.module.css';
import signUpFormStyles from './signUpFormLayout.module.css';

const requiredField = z.string().min(1, { error: () => msg('onboarding.enterpriseDemoForm.requiredError') });

const FormSchema = z.object({
  firstName: requiredField,
  lastName: requiredField,
  company: requiredField,
  jobTitle: requiredField,
  workEmail: z.email({ error: () => msg('onboarding.enterpriseDemoForm.emailError') }),
});

type FormData = z.infer<typeof FormSchema>;

export const EnterpriseDemoSignupForm = () => {
  const {
    submit,
    captcha,
    isPending,
    isError,
    isSuccess: isSubmitted,
  } = useMarketingFormSubmit({
    formId: MarketoForm.EnterpriseDemo,
    audience: 'visitor',
  });

  const onSubmit = ({ value }: { value: FormData }) => {
    submit(
      // Keys must match the marketo-form-submission-config allow-list for this form ID.
      {
        email: value.workEmail,
        firstName: value.firstName,
        lastName: value.lastName,
        company: value.company,
        title: value.jobTitle,
      },
      { onSuccess: trackSignupEnterpriseDemoFormSubmitted },
    );
  };

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      jobTitle: '',
      workEmail: '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit,
  });

  const { AppForm, AppField, Subscribe } = form;

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  if (isSubmitted) {
    return (
      <Alert status="success" className={styles.successMessage}>
        <Message id="onboarding.enterpriseDemoForm.successBody" />
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleFormSubmit} className={signUpFormStyles.form}>
      <AppForm>
        {isError && <Alert status="error">{msg('onboarding.enterpriseDemoForm.submitError')}</Alert>}
        <div className={signUpFormStyles.name}>
          <div>
            <AppField name="firstName">
              {(field) => (
                <field.TextInputField label={msg('onboarding.enterpriseDemoForm.firstNameLabel')} isRequired />
              )}
            </AppField>
          </div>
          <div>
            <AppField name="lastName">
              {(field) => (
                <field.TextInputField label={msg('onboarding.enterpriseDemoForm.lastNameLabel')} isRequired />
              )}
            </AppField>
          </div>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="company">
            {(field) => <field.TextInputField label={msg('onboarding.enterpriseDemoForm.companyLabel')} isRequired />}
          </AppField>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="jobTitle">
            {(field) => <field.TextInputField label={msg('onboarding.enterpriseDemoForm.jobTitleLabel')} isRequired />}
          </AppField>
        </div>
        <div className={signUpFormStyles.username}>
          <AppField name="workEmail">
            {(field) => (
              <field.TextInputField label={msg('onboarding.enterpriseDemoForm.emailLabel')} type="email" isRequired />
            )}
          </AppField>
        </div>
        <div className={signUpFormStyles.submit}>
          <Subscribe selector={(state) => state.isValid}>
            {(isValid) => (
              <Button
                variant="primary"
                isDisabled={!isValid || isPending}
                className={signUpFormStyles.submitButton}
                type="submit"
                isPending={isPending}
              >
                {msg('onboarding.enterpriseDemoForm.submit')}
              </Button>
            )}
          </Subscribe>
        </div>
        {captcha}
      </AppForm>
    </Form>
  );
};
